import type { AppDispatch } from "~/ui-rtk/app/store"
import { assetsApi } from "~/ui-rtk/api/assetsApi"
import { cacheAssetUrl } from "~/ui-rtk/app/slices/asset-cache.slice"
import { mapMediaChannelToSourceType } from "~/ui-rtk/components/atoms/MarathonImage/helpers"
import type { AssetSourceType } from "~/ui-rtk/components/atoms/MarathonImage/MarathonImage"

export type ResolveRowAssetsConfig = {
  /** Row field with the platform/source name (e.g. "media_channel"). */
  sourceTypeKey: string
  /** Row field with the external ref id (e.g. "post_id", "ad_id"). */
  externalRefIdKey: string
  /** Optional row field with a fallback platform URL. */
  urlKey?: string
  /** When true, organic source types (INSTAGRAM/TIKTOK/YOUTUBE) are used. */
  isOrganic?: boolean
}

export type ResolveRowAssetsContext = {
  dispatch: AppDispatch
  cachedAssetUrls: Record<string, string>
}

/**
 * Bulk-resolve assets for a set of rows in ONE request and write the resolved
 * URLs into the asset-cache slice (`cacheAssetUrl`). Already-cached keys are
 * skipped — only misses hit the network.
 *
 * MarathonImage reads from the same slice, so cells render images directly
 * from the cache. Because this util is awaited inside postProcess (before
 * `setProcessed`), the slice is populated before cells mount, eliminating
 * per-cell resolve fan-out.
 */
export async function resolveRowAssets<T extends Record<string, unknown>>(
  rows: T[],
  cfg: ResolveRowAssetsConfig,
  ctx: ResolveRowAssetsContext,
): Promise<void> {
  if (!rows.length) return

  const seen = new Set<string>()
  const assets: Array<{
    sourceType: AssetSourceType
    externalRefId: string
    url?: string
  }> = []

  for (const r of rows) {
    const rawSourceType = r[cfg.sourceTypeKey]
    const refId = r[cfg.externalRefIdKey]
    if (!rawSourceType || !refId) continue
    const sourceType = mapMediaChannelToSourceType(
      String(rawSourceType),
      cfg.isOrganic,
    )
    const cacheKey = `${sourceType}:${refId}`
    if (ctx.cachedAssetUrls[cacheKey]) continue
    if (seen.has(cacheKey)) continue
    seen.add(cacheKey)

    const url = cfg.urlKey ? (r[cfg.urlKey] as string | undefined) : undefined
    assets.push({
      sourceType,
      externalRefId: String(refId),
      ...(url ? { url } : {}),
    })
  }

  if (assets.length === 0) return

  // `dispatch(mutation.initiate(arg))` returns a subscription handle for the
  // mutation's cache entry. Without an explicit `.reset()`, every postProcess
  // run pins a new entry in the RTK Query store for the rest of the session
  // (filter/date-range changes re-trigger postProcess and accumulate). The
  // `finally` ensures cleanup even when `.unwrap()` rejects on 4xx/5xx.
  const queryResult = ctx.dispatch(
    assetsApi.endpoints.resolveAssets.initiate({ assets }),
  )
  try {
    const result = await queryResult.unwrap()
    for (const [cacheKey, asset] of Object.entries(result.results)) {
      if (!asset) continue
      let resolvedUrl: string | null = null
      if (asset.syncStatus === "SYNCED") {
        resolvedUrl = asset.url || asset.thumbnailUrl || null
      }
      if (!resolvedUrl) {
        const match = assets.find(
          a => `${a.sourceType}:${a.externalRefId}` === cacheKey,
        )
        resolvedUrl = match?.url || null
      }
      if (resolvedUrl) {
        ctx.dispatch(cacheAssetUrl({ key: cacheKey, url: resolvedUrl }))
      }
    }
  } catch {
    /* fall through — cells fall back to placeholder/url hint */
  } finally {
    queryResult.reset()
  }
}
